/**
 * BehavioralAnomalyDetectionAbnormalUserActionsPage.
 *
 * Fraud-ops surface for abnormal-user-action anomaly events:
 *  - Feed of detected events (actor, signal, severity, deviation bucket, timestamp).
 *  - Severity filter (all / low / medium / high / critical — computed from integer 0..100).
 *  - Signal filter (known taxonomy + "all").
 *  - Rules config: enable/disable known signals + per-signal severity thresholds.
 *
 * Design: Trackerity green-only accent (green-600 primary, green-50 tint). No emerald / blue /
 * orange / purple / dark mode / emoji. Light theme only. Inter font. 6-8px rounded corners.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAnomalyEventsList,
	useAnomalyEventCreate,
} from "./hooks";
import { KNOWN_SIGNALS, type AnomalyEvent } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const SELECT = INPUT;
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const LABEL =
	"text-xs font-medium uppercase tracking-wider text-zinc-500";

// Severity buckets — z-score-like tiers over the integer 0..100 scale.
// Kept in sync with the vitest data-layer tests.
export const SEVERITY_BUCKETS = [
	{ key: "low", label: "Low", min: 0, max: 24 },
	{ key: "medium", label: "Medium", min: 25, max: 49 },
	{ key: "high", label: "High", min: 50, max: 74 },
	{ key: "critical", label: "Critical", min: 75, max: 100 },
] as const;

export type SeverityBucketKey =
	(typeof SEVERITY_BUCKETS)[number]["key"];

export function bucketForSeverity(severity: number): SeverityBucketKey {
	const clamped = Math.max(0, Math.min(100, Math.round(severity)));
	for (const b of SEVERITY_BUCKETS) {
		if (clamped >= b.min && clamped <= b.max) return b.key;
	}
	return "low";
}

// Visual tint per bucket — GREEN-only accent. Non-critical rows use zinc-neutral chrome
// with a green bar for "active fraud signal"; critical/high get a bolder green stroke.
const BUCKET_BADGE: Record<SeverityBucketKey, string> = {
	low: "bg-zinc-100 text-zinc-700 border border-zinc-200",
	medium: "bg-green-50 text-green-700 border border-green-200",
	high: "bg-green-100 text-green-800 border border-green-300",
	critical: "bg-green-600 text-white border border-green-700",
};

function formatTs(unixSec: number): string {
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "-";
	const d = new Date(unixSec * 1000);
	return d.toISOString().replace("T", " ").replace(/\.\d+Z$/, "Z");
}

interface SignalRule {
	enabled: boolean;
	minSeverity: number;
}

function defaultRules(): Record<string, SignalRule> {
	const out: Record<string, SignalRule> = {};
	for (const s of KNOWN_SIGNALS) {
		out[s] = { enabled: true, minSeverity: 25 };
	}
	return out;
}

export function applyRules(
	events: AnomalyEvent[],
	rules: Record<string, SignalRule>,
): AnomalyEvent[] {
	return events.filter((e) => {
		const r = rules[e.signal];
		if (!r) return true; // unknown signal — surface by default
		if (!r.enabled) return false;
		return e.severity >= r.minSeverity;
	});
}

export function filterBySeverity(
	events: AnomalyEvent[],
	bucket: "all" | SeverityBucketKey,
): AnomalyEvent[] {
	if (bucket === "all") return events;
	return events.filter((e) => bucketForSeverity(e.severity) === bucket);
}

export function BehavioralAnomalyDetectionAbnormalUserActionsPage() {
	const [severityFilter, setSeverityFilter] = useState<
		"all" | SeverityBucketKey
	>("all");
	const [signalFilter, setSignalFilter] = useState<string>("all");
	const [rules, setRules] = useState<Record<string, SignalRule>>(() =>
		defaultRules(),
	);

	// Create form state
	const [actorId, setActorId] = useState("");
	const [eventId, setEventId] = useState("");
	const [newSignal, setNewSignal] = useState<string>(KNOWN_SIGNALS[0]);
	const [severity, setSeverity] = useState<number>(50);

	const listQuery = useAnomalyEventsList(
		signalFilter === "all" ? {} : { signal: signalFilter },
	);
	const createMut = useAnomalyEventCreate();

	const items = listQuery.data?.items ?? [];
	const visible = useMemo(() => {
		const viaRules = applyRules(items, rules);
		return filterBySeverity(viaRules, severityFilter);
	}, [items, rules, severityFilter]);

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!actorId.trim() || !eventId.trim()) return;
		createMut.mutate(
			{
				event_id: eventId.trim(),
				actor_id: actorId.trim(),
				signal: newSignal,
				severity,
				metadata: {},
				detected_at: Math.floor(Date.now() / 1000),
			},
			{
				onSuccess: () => {
					setEventId("");
					setActorId("");
				},
			},
		);
	};

	const updateRule = (signal: string, patch: Partial<SignalRule>) => {
		setRules((prev) => ({
			...prev,
			[signal]: { ...prev[signal], ...patch },
		}));
	};

	return (
		<main
			data-testid="behavioral-anomaly-detection-abnormal-user-actions-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Fraud ops / Behavioral anomalies
				</p>
				<h1 className="text-3xl font-semibold text-zinc-900">
					Abnormal user actions
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Continuous feed of behavioral anomalies flagged per tenant. Tune
					rules to raise or silence signals, filter by severity, and drill
					into per-actor bursts.
				</p>
			</header>

			{/* Controls row */}
			<section
				className={CARD}
				aria-label="Filters"
				data-testid="behavioral-anomaly-detection-abnormal-user-actions-filters"
			>
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
					<div>
						<label className={LABEL} htmlFor="severity-filter">
							Severity
						</label>
						<select
							id="severity-filter"
							className={SELECT}
							value={severityFilter}
							onChange={(e) =>
								setSeverityFilter(
									e.currentTarget.value as "all" | SeverityBucketKey,
								)
							}
							data-testid="behavioral-anomaly-detection-abnormal-user-actions-severity-filter"
						>
							<option value="all">All severities</option>
							{SEVERITY_BUCKETS.map((b) => (
								<option key={b.key} value={b.key}>
									{b.label} ({b.min}-{b.max})
								</option>
							))}
						</select>
					</div>
					<div>
						<label className={LABEL} htmlFor="signal-filter">
							Signal
						</label>
						<select
							id="signal-filter"
							className={SELECT}
							value={signalFilter}
							onChange={(e) => setSignalFilter(e.currentTarget.value)}
							data-testid="behavioral-anomaly-detection-abnormal-user-actions-signal-filter"
						>
							<option value="all">All signals</option>
							{KNOWN_SIGNALS.map((s) => (
								<option key={s} value={s}>
									{s.replace(/_/g, " ")}
								</option>
							))}
						</select>
					</div>
					<div className="flex items-end">
						<p
							className="text-sm text-zinc-600"
							data-testid="behavioral-anomaly-detection-abnormal-user-actions-count"
						>
							<span className="font-semibold text-zinc-900">
								{visible.length}
							</span>{" "}
							visible of{" "}
							<span className="font-semibold text-zinc-900">
								{items.length}
							</span>{" "}
							detected
						</p>
					</div>
				</div>
			</section>

			{/* Rules config */}
			<section
				className={CARD}
				aria-label="Rules config"
				data-testid="behavioral-anomaly-detection-abnormal-user-actions-rules"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Detection rules
				</h2>
				<p className="mt-1 text-sm text-zinc-600">
					Enable or silence signal types and set the minimum severity needed
					for a row to surface.
				</p>
				<ul
					className="mt-4 divide-y divide-zinc-200"
					data-testid="behavioral-anomaly-detection-abnormal-user-actions-rules-list"
				>
					{KNOWN_SIGNALS.map((s) => {
						const r = rules[s];
						return (
							<li
								key={s}
								className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between"
								data-testid={`behavioral-anomaly-detection-abnormal-user-actions-rule-${s}`}
							>
								<div className="flex items-center gap-3">
									<input
										type="checkbox"
										checked={r.enabled}
										onChange={(e) =>
											updateRule(s, { enabled: e.currentTarget.checked })
										}
										aria-label={`enable ${s}`}
										className="h-4 w-4 rounded border-zinc-300 text-green-600 focus:ring-green-600"
										data-testid={`behavioral-anomaly-detection-abnormal-user-actions-rule-enabled-${s}`}
									/>
									<span className="font-mono text-xs text-zinc-700">
										{s}
									</span>
								</div>
								<div className="flex items-center gap-3">
									<label className={LABEL} htmlFor={`min-sev-${s}`}>
										Min severity
									</label>
									<input
										id={`min-sev-${s}`}
										type="number"
										min={0}
										max={100}
										step={1}
										value={r.minSeverity}
										onChange={(e) => {
											const n = Number.parseInt(e.currentTarget.value, 10);
											if (Number.isFinite(n)) {
												updateRule(s, {
													minSeverity: Math.max(0, Math.min(100, n)),
												});
											}
										}}
										className="w-20 rounded-[6px] border border-zinc-300 bg-white px-2 py-1 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
										data-testid={`behavioral-anomaly-detection-abnormal-user-actions-rule-minsev-${s}`}
									/>
								</div>
							</li>
						);
					})}
				</ul>
			</section>

			{/* Report event (ops manual entry) */}
			<section
				className={CARD}
				aria-label="Report anomaly event"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Report anomaly
				</h2>
				<p className="mt-1 text-sm text-zinc-600">
					Manually ingest a behavioral anomaly event (for backfill or ops
					replay).
				</p>
				<form
					onSubmit={handleCreate}
					className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-5"
					data-testid="behavioral-anomaly-detection-abnormal-user-actions-create-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="event_id"
						value={eventId}
						onChange={(e) => setEventId(e.currentTarget.value)}
						disabled={createMut.isPending}
						data-testid="behavioral-anomaly-detection-abnormal-user-actions-event-id"
					/>
					<input
						type="text"
						className={INPUT}
						placeholder="actor_id"
						value={actorId}
						onChange={(e) => setActorId(e.currentTarget.value)}
						disabled={createMut.isPending}
						data-testid="behavioral-anomaly-detection-abnormal-user-actions-actor-id"
					/>
					<select
						className={SELECT}
						value={newSignal}
						onChange={(e) => setNewSignal(e.currentTarget.value)}
						disabled={createMut.isPending}
						data-testid="behavioral-anomaly-detection-abnormal-user-actions-new-signal"
					>
						{KNOWN_SIGNALS.map((s) => (
							<option key={s} value={s}>
								{s.replace(/_/g, " ")}
							</option>
						))}
					</select>
					<input
						type="number"
						min={0}
						max={100}
						step={1}
						className={INPUT}
						placeholder="severity (0-100)"
						value={severity}
						onChange={(e) => {
							const n = Number.parseInt(e.currentTarget.value, 10);
							if (Number.isFinite(n)) {
								setSeverity(Math.max(0, Math.min(100, n)));
							}
						}}
						disabled={createMut.isPending}
						data-testid="behavioral-anomaly-detection-abnormal-user-actions-new-severity"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={
							createMut.isPending || !actorId.trim() || !eventId.trim()
						}
						data-testid="behavioral-anomaly-detection-abnormal-user-actions-submit"
					>
						{createMut.isPending ? "Reporting." : "Report"}
					</button>
				</form>
				{createMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{createMut.error.message}
					</p>
				) : null}
			</section>

			{/* Feed */}
			<section
				className={CARD}
				aria-label="Anomaly feed"
			>
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Anomaly feed
					</h2>
					<button
						type="button"
						className={BTN_GHOST}
						onClick={() => listQuery.refetch()}
						disabled={listQuery.isFetching}
						data-testid="behavioral-anomaly-detection-abnormal-user-actions-refresh"
					>
						{listQuery.isFetching ? "Refreshing." : "Refresh"}
					</button>
				</div>
				{listQuery.isLoading ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="behavioral-anomaly-detection-abnormal-user-actions-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-4 text-sm text-red-600"
						role="alert"
						data-testid="behavioral-anomaly-detection-abnormal-user-actions-error"
					>
						{listQuery.error.message}
					</p>
				) : visible.length === 0 ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="behavioral-anomaly-detection-abnormal-user-actions-empty"
					>
						No anomalies match the current filters.
					</p>
				) : (
					<div
						className="mt-4 overflow-x-auto"
						data-testid="behavioral-anomaly-detection-abnormal-user-actions-list"
					>
						<table className="min-w-full text-left text-sm">
							<thead>
								<tr className="border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500">
									<th className="py-2 pr-4 font-medium">Actor</th>
									<th className="py-2 pr-4 font-medium">Signal</th>
									<th className="py-2 pr-4 font-medium">Severity</th>
									<th className="py-2 pr-4 font-medium">Bucket</th>
									<th className="py-2 pr-4 font-medium">Detected</th>
									<th className="py-2 pr-4 font-medium">Event</th>
								</tr>
							</thead>
							<tbody>
								{visible.map((row) => {
									const bucket = bucketForSeverity(row.severity);
									return (
										<tr
											key={row.id}
											className="border-b border-zinc-100"
											data-testid={`behavioral-anomaly-detection-abnormal-user-actions-row-${row.id}`}
										>
											<td className="py-2 pr-4 font-mono text-xs text-zinc-700">
												{row.actor_id}
											</td>
											<td className="py-2 pr-4 text-zinc-900">
												{row.signal.replace(/_/g, " ")}
											</td>
											<td className="py-2 pr-4 font-mono text-xs text-zinc-800">
												{row.severity}
											</td>
											<td className="py-2 pr-4">
												<span
													className={`inline-flex items-center rounded-[6px] px-2 py-0.5 text-xs font-medium ${BUCKET_BADGE[bucket]}`}
													data-testid={`behavioral-anomaly-detection-abnormal-user-actions-bucket-${row.id}`}
												>
													{bucket}
												</span>
											</td>
											<td className="py-2 pr-4 font-mono text-xs text-zinc-600">
												{formatTs(row.detected_at)}
											</td>
											<td className="py-2 pr-4 font-mono text-xs text-zinc-500">
												{row.event_id}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default BehavioralAnomalyDetectionAbnormalUserActionsPage;
